import {fillArray, noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {ActionIcon, createStyles, Text} from '@mantine/core';
import React, {useMemo} from 'react';

import {pluralize} from 'BenchlingUtil';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {ColumnConstraint} from 'notebook/tables/constants/TableColumnConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import {
  BaseCondition,
  ConditionValidationState,
  isPersistedCondition,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import {MAX_CONDITIONS_PER_PROCEDURE_RUN} from 'studies/typed/shared/procedure-runs/execution-status/useMethodExecutionLimitState';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {getConditionNameSuffix} from './conditionArchiveDisplayUtils';
import {DEFAULT_CONDITION_VALIDATION_STATE} from './conditionUtils';
import {EditConditionDescriptionModal} from './EditConditionDescriptionModal';
import {
  ConditionRowData,
  confirmDeleteConditions,
  getConditionArchiveState,
  getDescErrorTriangle,
  getNameErrorTriangle,
  isFullyPendingRemoved,
  makeValueCell,
  updateTableCells,
} from './replicateTableUtils';
import {useConditionDescriptionDraft} from './useConditionDescriptionDraft';

const DEFAULT_TABLE_CID = 'doe-replicates-transposed';

/** One table row: persisted values plus optional display overrides. */
interface TransposedReplicateTableRow {
  realIndex: number;
  name: string;
  nameDisplay?: string;
  /** Editable count, excluding archived flows. */
  numReplicates: number;
  /** Displayed count, including archived flows when they are visible. */
  displayedReplicateCount: number;
  description: string;
  validationState: ConditionValidationState;
  locked: boolean;
}

interface MakeInitialReplicateTableOptions {
  locked: boolean;
  procedureType: ProcedureType;
  openEditDescriptionModal: (conditionIndex: number) => void;
  classes: ReturnType<typeof useStyles>['classes'];
  maxRows?: number;
}

export interface ArchivedDisplayOptions {
  showArchived: boolean;
  /** Fully pending-removed replicate lane count keyed by condition index. */
  pendingRemovedByConditionIndex?: ReadonlyMap<number, number>;
}

/** Rows already derived for a parent summary, so the table does not derive them again. */
interface PrecomputedTransposedReplicateRows {
  rows: Array<TransposedReplicateTableRow>;
  hiddenConditionIndices: Set<number>;
}

export function buildTransposedReplicateTableRows<TCondition extends BaseCondition>(
  conditions: Array<TCondition>,
  {isPlan, archivedDisplay}: {isPlan: boolean; archivedDisplay?: ArchivedDisplayOptions}
): PrecomputedTransposedReplicateRows {
  const isArchiveDisplayEnabled = archivedDisplay != null;
  const showArchived = archivedDisplay?.showArchived ?? false;
  const pendingRemovedByConditionIndex = archivedDisplay?.pendingRemovedByConditionIndex;
  const rows: Array<TransposedReplicateTableRow> = [];
  const hiddenConditionIndices = new Set<number>();

  conditions.forEach((condition, realIndex) => {
    const {isFullyArchived, liveReplicateCount, archivedReplicateCount} = getConditionArchiveState(
      condition,
      isArchiveDisplayEnabled
    );
    const pendingRemovedReplicateCount = pendingRemovedByConditionIndex?.get(realIndex) ?? 0;
    const remainingLive = Math.max(0, liveReplicateCount - pendingRemovedReplicateCount);
    const isFullyPending = isFullyPendingRemoved(liveReplicateCount, pendingRemovedReplicateCount);
    const shouldHideWhenArchivedOff = isArchiveDisplayEnabled && (isFullyArchived || isFullyPending);
    if (shouldHideWhenArchivedOff && !showArchived) {
      hiddenConditionIndices.add(realIndex);
      return;
    }
    const nameDisplay = `${condition.name}${getConditionNameSuffix({
      isArchived: isFullyArchived,
      isPendingArchive: isFullyPending,
    })}`;
    rows.push({
      realIndex,
      name: condition.name,
      nameDisplay,
      numReplicates: condition.numReplicates,
      displayedReplicateCount: showArchived ? liveReplicateCount + archivedReplicateCount : remainingLive,
      description: condition.description ?? '',
      validationState: condition.validationState ?? DEFAULT_CONDITION_VALIDATION_STATE,
      locked: isPlan ? false : isPersistedCondition(condition),
    });
  });

  return {rows, hiddenConditionIndices};
}

/** Summarizes the same rows a parent rendered, so a header cannot drift from the table. */
export function summarizeTransposedReplicateRows(
  rows: Array<TransposedReplicateTableRow>,
  procedureType: ProcedureType
): string {
  const totalReplicates = rows.reduce((acc, row) => acc + row.displayedReplicateCount, 0);
  const conditionWord = getProcedureRunConditionTextForProcedureType(procedureType);
  return `${rows.length} ${pluralize(rows.length, conditionWord)}, ${totalReplicates} ${pluralize(
    totalReplicates,
    'replicate'
  )}`;
}

function makeInitialReplicateTable(
  tableName: string,
  rows: Array<TransposedReplicateTableRow>,
  options: MakeInitialReplicateTableOptions
): Table {
  const {locked, procedureType, openEditDescriptionModal, classes, maxRows} = options;
  const validationStatePerCondition = rows.map((row) => row.validationState);
  const isLockedPerCondition = rows.map((row) => row.locked);

  const makeNameCell = (row: TransposedReplicateTableRow, visibleIndex: number): Cell => ({
    text: row.name,
    locked: locked || isLockedPerCondition[visibleIndex],
    align: 'left',
    bold: false,
    userAnnotationIndicator: getNameErrorTriangle(validationStatePerCondition, visibleIndex, procedureType),
    ...(row.nameDisplay != null ? {formattedValue: <span>{row.nameDisplay}</span>} : {}),
  });

  const makeDescCell = (row: TransposedReplicateTableRow, visibleIndex: number): Cell => {
    const isCellLocked = locked || isLockedPerCondition[visibleIndex];
    const text = row.description;
    return {
      text,
      locked: isCellLocked,
      align: 'left',
      bold: false,
      userAnnotationIndicator: getDescErrorTriangle(validationStatePerCondition, visibleIndex, procedureType),
      formattedValue: (
        <div className={classes.descriptionCellContent}>
          <span
            className={classes.descriptionCellText}
            data-ellipsized="true"
            {...getTestProps({component: 'TransposedReplicateTable', element: 'descriptionText'})}
          >
            {text}
          </span>
          {!isCellLocked ? (
            <ActionIcon
              size="xs"
              variant="ghost"
              aria-label="Edit description"
              {...getTestProps({component: 'TransposedReplicateTable', element: 'editDescriptionButton'})}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                openEditDescriptionModal(row.realIndex);
              }}
            >
              <BenchlingIcon icon="edit" className={classes.editDescriptionIcon} />
            </ActionIcon>
          ) : null}
        </div>
      ),
    };
  };

  const numConditions = rows.length;
  const conditionRows = fillArray(numConditions, (visibleIndex) => {
    const row = rows[visibleIndex];
    return [
      makeNameCell(row, visibleIndex),
      makeValueCell(
        row.numReplicates,
        visibleIndex,
        locked,
        isLockedPerCondition,
        row.displayedReplicateCount !== row.numReplicates ? (
          <span>{row.displayedReplicateCount}</span>
        ) : undefined
      ),
      makeDescCell(row, visibleIndex),
    ];
  });

  return {
    name: tableName,
    widths: [100, 45, 95],
    rowData: fillArray(
      numConditions,
      (visibleIndex): ConditionRowData => ({
        processDevelopmentCustomIndex: rows[visibleIndex].realIndex,
        isFinalized: rows[visibleIndex].locked,
      })
    ),
    columnLabels: ['Name', 'Reps', 'Description'],
    columnConstraints: fillArray(
      3,
      (): ColumnConstraint => ({
        editLabelDisabled: true,
      })
    ),
    headerCol: false,
    headerRow: false,
    table: conditionRows,
    maxRows,
    lockedDims: {allowAddRows: !locked, allowRemoveRows: !locked, allowEditCols: false},
    allowRowRemoval: !locked,
    allowAddRows: !locked,
    allowColumnEdits: false,
    _hideRename: true,
  };
}

interface TransposedReplicateTableProps<TCondition extends BaseCondition> {
  conditions: Array<TCondition>;
  setConditions: (conditions: Array<TCondition>, prevIndexes: Array<number | null>) => void;
  experimentalInputs: ExperimentalInputs | null;
  setExperimentalInputs: (experimentalInputs: ExperimentalInputs) => void;
  locked: boolean;
  procedureType: ProcedureType;
  newConditionCoveredMethodIds?: ReadonlyArray<string>;
  isPlan?: boolean;
  archivedDisplay?: ArchivedDisplayOptions;
  dataTestAttrs?: {[key: string]: string};
  /** When provided, the table renders these rows instead of deriving them from conditions. */
  precomputedRows?: PrecomputedTransposedReplicateRows;
}

/**
 * Renders process development condition settings in a table layout where each row corresponds to one
 * condition. Columns are name, number of replicates, and description.
 */
export function TransposedReplicateTable<TCondition extends BaseCondition>(
  props: TransposedReplicateTableProps<TCondition>
): JSX.Element {
  const {classes} = useStyles();
  const isPlan = props.isPlan ?? false;
  const resolvedRows = useMemo(() => {
    if (props.precomputedRows != null) {
      return props.precomputedRows;
    }
    return buildTransposedReplicateTableRows(props.conditions, {
      isPlan,
      archivedDisplay: props.archivedDisplay,
    });
  }, [props.precomputedRows, props.conditions, isPlan, props.archivedDisplay]);
  const {rows, hiddenConditionIndices} = resolvedRows;
  const maxRows = Math.max(0, MAX_CONDITIONS_PER_PROCEDURE_RUN - hiddenConditionIndices.size);
  const {
    isEditDescriptionModalOpen,
    descriptionDraft,
    setDescriptionDraft,
    openEditDescriptionModal,
    closeEditDescriptionModal,
    saveDescriptionDraft,
  } = useConditionDescriptionDraft({
    conditions: props.conditions,
    setConditions: props.setConditions,
  });

  const table =
    rows.length > 0
      ? makeInitialReplicateTable(
          getProcedureRunConditionTextForProcedureType(props.procedureType, {capital: true, plural: true}),
          rows,
          {
            locked: props.locked,
            procedureType: props.procedureType,
            openEditDescriptionModal,
            classes,
            maxRows,
          }
        )
      : null;

  function handleDeleteRows(
    index: number,
    start: number,
    end: number,
    doDelete: (index: number, start: number, end: number) => void,
    isProcedureRunPlan: boolean
  ): void {
    const conditionsToRemove = rows.slice(start, end + 1).map((row) => props.conditions[row.realIndex]);
    confirmDeleteConditions({
      unitWord: 'row',
      conditionsToRemove,
      experimentalInputs: props.experimentalInputs,
      procedureType: props.procedureType,
      isProcedureRunPlan,
      doDelete: () => {
        doDelete(index, start, end);
      },
    });
  }

  return (
    <div {...getTestProps({component: 'TransposedReplicateTable', element: 'root'})}>
      <div className={classes.tableWrapper}>
        {table == null ? (
          <Text className={classes.emptyState}>No conditions to display</Text>
        ) : (
          <SimpleTableEditable
            {...standaloneProps}
            dataTestAttrs={props.dataTestAttrs}
            hideFormulaBox={true}
            isCompactBottomSpacing={true}
            canAddRemoveColumns={false}
            locked={props.locked}
            table={table}
            cid={DEFAULT_TABLE_CID}
            updateHandler={(_, {table: nextTable}: {table: Table}) => {
              updateTableCells(
                nextTable,
                props.conditions,
                props.setConditions,
                props.procedureType,
                props.newConditionCoveredMethodIds,
                hiddenConditionIndices
              );
            }}
            storeState={noop}
            restoreState={noop}
            isCollapsible={false}
            suppressTitleBar={true}
            hideRowCount={true}
            onDeleteRows={(index, start, end, doDelete) => {
              handleDeleteRows(index, start, end, doDelete, isPlan);
            }}
          />
        )}
      </div>
      <EditConditionDescriptionModal
        opened={isEditDescriptionModalOpen}
        descriptionDraft={descriptionDraft}
        onClose={closeEditDescriptionModal}
        onDescriptionDraftChange={setDescriptionDraft}
        onSave={saveDescriptionDraft}
      />
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  tableWrapper: {
    '& .mediocre-tableEditable-container': {
      padding: 0,
    },
  },
  emptyState: {
    color: theme.colors.gray[6],
    fontSize: theme.fontSizes.sm,
    padding: `${theme.spacing.sm}px 0`,
  },
  descriptionCellContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xxs,
    width: '100%',
    minWidth: 0,
  },
  descriptionCellText: {
    minWidth: 0,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  editDescriptionIcon: {
    color: Color.GRAY_700,
  },
}));
